import React, { useState } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Grid,
    Paper,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    People as PeopleIcon,
    Group as GroupIcon,
    Add as AddIcon,
} from '@mui/icons-material';

const UserRolesSection = ({
    stats,
    loading,
    permissions,
    onFetchStats,
    onFetchPermissions,
    onCreateRole
}) => {
    const [expanded, setExpanded] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [newRoleName, setNewRoleName] = useState('');
    const [selectedPerms, setSelectedPerms] = useState([]);

    const handleExpand = (e, exp) => {
        setExpanded(exp);
        if (exp) {
            onFetchStats();
            onFetchPermissions();
        }
    };

    const handleTogglePerm = (id) => {
        setSelectedPerms(prev =>
            prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
        );
    };

    const handleSubmit = async () => {
        try {
            await onCreateRole(newRoleName, selectedPerms);
            setOpenDialog(false);
            setNewRoleName('');
            setSelectedPerms([]);
        } catch (e) { }
    };

    return (
        <Accordion
            expanded={expanded}
            onChange={handleExpand}
            sx={{
                mb: 2,
                borderRadius: '0.75rem',
                border: '1px solid #d0dbe7',
                boxShadow: 'none',
                '&:before': { display: 'none' },
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#800020' }} />}
                sx={{
                    bgcolor: '#f6f7f8',
                    borderRadius: expanded ? '0.75rem 0.75rem 0 0' : '0.75rem',
                    '&:hover': { bgcolor: '#f0f0f0' },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <PeopleIcon sx={{ color: '#800020' }} />
                    <Typography sx={{ fontWeight: 600, color: '#0e141b' }}>Usuarios y Permisos</Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
                {stats && (
                    <Box>
                        <Grid container spacing={2} sx={{ mb: 4 }}>
                            {[
                                { label: 'Total', value: stats.summary.total_users, bg: '#e3f2fd', color: '#1976d2' },
                                { label: 'Activos', value: stats.summary.active_users, bg: '#e8f5e9', color: '#2e7d32' },
                                { label: 'Staff', value: stats.summary.staff_users, bg: '#fff3e0', color: '#f57c00' },
                                { label: 'Super', value: stats.summary.superusers, bg: '#fce4ec', color: '#c2185b' },
                            ].map((s, i) => (
                                <Grid item xs={6} sm={3} key={i}>
                                    <Paper sx={{ p: 2, bgcolor: s.bg, border: `1px solid ${s.color}40`, textAlign: 'center' }}>
                                        <Typography sx={{ color: s.color, fontSize: '0.75rem', fontWeight: 600 }}>{s.label}</Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 700 }}>{s.value}</Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Roles (Grupos)</Typography>
                            <Button size="small" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
                                Crear Rol
                            </Button>
                        </Box>

                        <Grid container spacing={2}>
                            {stats.groups.map((group) => (
                                <Grid item xs={12} md={6} key={group.id}>
                                    <Paper sx={{ p: 2, border: '1px solid #d0dbe7' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <GroupIcon fontSize="small" sx={{ color: '#800020' }} />
                                            <Typography sx={{ fontWeight: 600 }}>{group.name}</Typography>
                                            <Chip label={group.user_count} size="small" sx={{ ml: 'auto' }} />
                                        </Box>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {(group.permissions_by_app ? Object.values(group.permissions_by_app).flatMap(app => Object.values(app).flat()) : []).slice(0, 5).map((p, i) => (
                                                <Chip key={i} label={p.name} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                                            ))}
                                            {(group.permission_count > 5) && <Chip label="..." size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />}
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

                <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                    <DialogTitle>Nuevo Rol y Permisos</DialogTitle>
                    <DialogContent>
                        <TextField
                            label="Nombre del Rol" fullWidth required sx={{ mt: 1, mb: 2 }}
                            value={newRoleName}
                            onChange={(e) => setNewRoleName(e.target.value)}
                        />
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Seleccionar Permisos:</Typography>
                        <Grid container spacing={2}>
                            {Object.entries(permissions).map(([app, perms]) => (
                                <Grid item xs={12} sm={6} md={4} key={app}>
                                    <Paper sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>{app}</Typography>
                                        <List dense>
                                            {Object.values(perms).flat().map((p) => (
                                                <ListItem key={p.id} button onClick={() => handleTogglePerm(p.id)} sx={{ py: 0 }}>
                                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                                        <Checkbox
                                                            edge="start" size="small"
                                                            checked={selectedPerms.includes(p.id)}
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText primary={p.name} primaryTypographyProps={{ fontSize: '0.75rem' }} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                        <Button onClick={handleSubmit} variant="contained" sx={{ bgcolor: '#800020' }}>Crear Rol</Button>
                    </DialogActions>
                </Dialog>
            </AccordionDetails>
        </Accordion>
    );
};

export default UserRolesSection;
